import type React from 'react';
import { useEffect, useState } from 'react';

type ExtraOpts<T> = {
  dispose: () => void; // dispose the state
  initSave: T; // the initial value from localStorage
};

type BasicTypes = string | number | boolean | unknown[] | Record<string, unknown> | null | undefined;

type MyReturnType<T extends BasicTypes> = [
  // current state
  state: T,
  // set state
  setState: React.Dispatch<React.SetStateAction<T | undefined>>,
  // extra options for state management
  extraOpts: ExtraOpts<T>,
];

export const ZNGLY_LOCAL_STORAGE_KEY = 'zngly:';

const disposeFn = (key: string) => {
  window.localStorage.removeItem(key);
};

const getItem = (key: string) => {
  const localState = window.localStorage.getItem(key);
  if (localState && typeof localState === 'string' && localState.length > 0)
    try {
      return JSON.parse(localState);
    } catch (error) {
      disposeFn(key);
    }
  return false;
};

export const getLocalStorageItem = (key: string) => {
  return getItem(ZNGLY_LOCAL_STORAGE_KEY + key);
};

export const useLocalState = <T extends BasicTypes>(key: string, value: T): MyReturnType<T> => {
  const [state, setState] = useState<T>();

  const updatedKey = ZNGLY_LOCAL_STORAGE_KEY + key;

  // init state from localStorage
  const getInitSave = () => {
    const localState = getItem(updatedKey);
    if (localState !== false || !!localState) return localState;

    return value;
  };
  const [initSave] = useState(getInitSave());

  // load state
  useEffect(() => {
    const localState = getItem(updatedKey);

    if (!!localState) setState(localState);
    else setState(value);
  }, []);

  // persist state
  useEffect(() => {
    if (state === undefined) return;
    if (state !== getItem(updatedKey)) window.localStorage.setItem(updatedKey, JSON.stringify(state));
  }, [state]);

  const extraOpts: ExtraOpts<T> = {
    dispose: () => {
      disposeFn(updatedKey);
    },
    initSave,
  };

  return [state ?? value, setState, extraOpts];
};

export default useLocalState;
